import { FC, useEffect, useState } from 'react'
import style from './style.m.less'
import { useTranslation } from 'react-i18next'
import { CenteredSpin, Checkbox, PrimaryButton } from '../../../ui'
import { Alert, Modal } from 'antd'
import { IGeneratingObject } from '../../../../store/generators/IGeneratingObject'
import { useAppSelector } from '../../../../store/hooks'
import request from '../../../../services/request'
import {EUserRole, EUserType} from "../../../../constants/approval";
import {isExpired} from "../../../helpers";

interface IConfirmConnectionModal {
    isModalVisible: boolean
    generator: IGeneratingObject
    onClose(): void
    onCloseAndReload(): void
}

const ConfirmConnectionModal: FC<IConfirmConnectionModal> = ({
    isModalVisible,
    generator,
    onClose,
    onCloseAndReload,
}) => {
    const { t } = useTranslation()
    const userData = useAppSelector((state) => state.user.data)
    const userId = userData.uid
    const userRole = userData.user_role
    const isTSO = userRole == EUserRole.TSO
    const isSignExpired = isTSO && isExpired(userData?.certificate_data.token_expiration)
    // У пользователя должна быть МЧД
    const needAttorney = userData?.user_type === EUserType.UrLico && isTSO && !userData?.without_attorney
    // У пользователя должна быть МЧД, но её нет
    const isEmptyAttorney = needAttorney && !userData?.machine_readable_attorney
    // У пользователя есть МЧД и у неё истёк срок
    const isAttorneyExpired = needAttorney
        && !!userData?.machine_readable_attorney
        && isExpired(userData?.machine_readable_attorney.validity_period, true)



    const canConfirmTso = generator?.tso?.uid === userId
    const canConfirmGp = generator?.gp?.uid === userId

    const tsoNotConfirmed = canConfirmTso && !generator?.tso?.is_confirmed
    const gpNotConfirmed = canConfirmGp && !generator?.gp?.is_confirmed

    const [tsoChecked, setTsoChecked] = useState(tsoNotConfirmed)
    useEffect(() => {
        setTsoChecked(tsoNotConfirmed)
    }, [tsoNotConfirmed])

    const [gpChecked, setGpChecked] = useState(gpNotConfirmed)
    useEffect(() => {
        setGpChecked(gpNotConfirmed)
    }, [gpNotConfirmed])

    const [loading, setLoading] = useState(false)
    const handleOk = async () => {
        try {
            setLoading(true)
            await request.post(`/api/generators/v1/go-confirm`, {
                generator_id: generator.generator_id,
                confirm_tso: canConfirmTso ? tsoChecked : undefined,
                confirm_gp: canConfirmGp ? gpChecked : undefined,
            })
        } finally {
            setLoading(false)
            onCloseAndReload()
        }
    }

    const handleReject = async () => {
        try {
            setLoading(true)
            await request.post(`/api/generators/v1/go-confirm`, {
                generator_id: generator.generator_id,
                confirm_tso: canConfirmTso ? false : undefined,
                confirm_gp: canConfirmGp ? false : undefined,
            })
        } finally {
            setLoading(false)
            onCloseAndReload()
        }
    }

    return (
        <>
            {loading && <CenteredSpin />}
            <Modal
                className={style.modal}
                closable={true}
                footer={null}
                centered
                visible={isModalVisible}
                onCancel={onClose}
            >
                <div className={style.modalHeader}>Подтверждение принадлежности к ЗГО</div>
                <div className={style.itemWrapper}>
                    <div className={style.itemName}>ЗГО</div>
                    <div className={style.itemValue}>{generator?.name}</div>
                </div>
                <div className={style.itemWrapper}>
                    <div className={style.itemName}>Ваша роль</div>
                    <div className={style.itemValue}>
                        {canConfirmTso && (
                            <Checkbox
                                checked={tsoChecked}
                                className={style.checkbox}
                                onChange={(event) => setTsoChecked(event.target.checked)}
                                disabled={!canConfirmGp}
                            >
                                ТСО
                            </Checkbox>
                        )}
                        {canConfirmGp && (
                            <Checkbox
                                checked={gpChecked}
                                className={style.checkbox}
                                onChange={(event) => setGpChecked(event.target.checked)}
                                disabled={!canConfirmTso}
                            >
                                ГП
                            </Checkbox>
                        )}
                    </div>
                </div>
                {canConfirmTso && canConfirmGp && (
                    <Alert
                        className={style.alert}
                        message="Укажите принадлежность ЗГО к вашей учетной записи. Для отклонения принадлежности ЗГО к ТСО/ГП уберите обе отметки и подтвердите действие."
                        type="info"
                    />
                )}
                {isSignExpired && (
                    <Alert
                        className={style.signError}
                        message={'Истек срок действия ЭП'}
                        type="error"
                        showIcon
                    />
                )}
                {(isEmptyAttorney || isAttorneyExpired) && (
                    <Alert
                        className={style.signError}
                        message={isEmptyAttorney ? t('profile.attorneyIsMissing') : t('profile.attorneyIsExpired')}
                        type="error"
                        showIcon
                    />
                )}
                <div className={style.buttonsBlock}>
                    {canConfirmTso && canConfirmGp ? (
                        <PrimaryButton
                            onClick={onClose}
                            value="Отмена"
                            isCancel={!isSignExpired && !isEmptyAttorney && !isAttorneyExpired}
                            customStyle={!isSignExpired && !isEmptyAttorney && !isAttorneyExpired ? { padding: '8px 16px' } : {}}
                        />
                    ) : (
                        <PrimaryButton
                            onClick={handleReject}
                            value="Отклонить"
                            isCancel={!isSignExpired && !isEmptyAttorney && !isAttorneyExpired}
                            customStyle={
                                !isSignExpired && !isEmptyAttorney && !isAttorneyExpired ? {
                                    padding: '8px 16px',
                                    color: '#FF4D4F',
                                    border: '1px solid #FF7875',
                                } : {}
                            }
                            disabled={isSignExpired || isEmptyAttorney || isAttorneyExpired}
                        />
                    )}
                    <PrimaryButton
                        onClick={handleOk}
                        value="Подтвердить"
                        customStyle={{ padding: '8px 16px' }}
                        disabled={isSignExpired || isEmptyAttorney || isAttorneyExpired}
                    />
                </div>
            </Modal>
        </>
    )
}

export default ConfirmConnectionModal
